import { HealthDataType } from '@sancare/typescript-commons/src/types/health-data'

import { FalseJustificationItem, TextualJustificationChunk, TextualJustificationItem } from '@/misc/types'

import { getHealthDataLabel } from '../health-data/documentLabels'

export interface TextualPart {
  key: string
  title: string
  mainContentType: string
  docType: 'report' | 'textualHealthEntry'
  docId: number
  falseJustifications: FalseJustificationItem[]
  paragraphs: TextualJustificationChunk[][]
  // Whether the justification's first/last chunk stops short of the source document's actual
  // start/end, i.e. the excerpt itself is truncated (as opposed to a "[...]" gap between chunks).
  startTruncated: boolean
  endTruncated: boolean
}

// Consecutive chunks are grouped into one paragraph ; a gap between two chunks' offsets means the
// backend skipped over irrelevant text, so a new paragraph (with a "[...]" separator) starts.
// Assumes chunks are already ordered by ascending offsetStart, as the backend sends them.
export function buildParagraphs(chunks: TextualJustificationChunk[]): TextualJustificationChunk[][] {
  const paragraphs: TextualJustificationChunk[][] = []
  let previousEnd: number | null = null

  chunks.forEach((chunk) => {
    if (previousEnd === null || chunk.offsetStart !== previousEnd) {
      paragraphs.push([])
    }
    paragraphs.at(-1)!.push(chunk)
    previousEnd = chunk.offsetStart + chunk.text.length
  })

  return paragraphs
}

export function computeTruncation(chunks: TextualJustificationChunk[], contentLength: number): { startTruncated: boolean, endTruncated: boolean } {
  if (chunks.length === 0) {
    return { startTruncated: false, endTruncated: false }
  }
  const lastChunk = chunks[chunks.length - 1]
  return {
    startTruncated: chunks[0].offsetStart > 0,
    endTruncated: lastChunk.offsetStart + lastChunk.text.length < contentLength,
  }
}

export function buildTextualPart(just: TextualJustificationItem, allFalseJustifications: FalseJustificationItem[], untitledDocumentLabel: string): TextualPart | null {
  if (just.documentId === null) {
    return null
  }
  const chunks = Array.isArray(just.content) ? just.content : []
  const docType = just.sourceType === 'report' ? 'report' : 'textualHealthEntry'
  const title = docType === 'report'
    ? (just.title || untitledDocumentLabel)
    : getHealthDataLabel(just.title ?? '')

  return {
    key: `${docType}-${just.documentId}`,
    title,
    mainContentType: docType === 'report' ? HealthDataType.REPORT : (just.title ?? ''),
    docType,
    docId: just.documentId,
    falseJustifications: allFalseJustifications.filter(
      (fj) => fj.documentType === docType && fj.documentId === just.documentId
    ),
    paragraphs: buildParagraphs(chunks),
    ...computeTruncation(chunks, just.contentLength),
  }
}
